import { parseFilter, matchesFilter, type FilterContext } from './filters'
import { areaLabel, availableStages, hasQaGate, healthCategory, isLiveStage, isQaStage, isReleasedStage, PRIORITY_ORDER, isRollout } from './stage'
import { orderOptions } from './field-order'
import type { IssueField, WorkIssue } from '@/types/github'

/** What a view pool may read beyond the universe. */
export type PoolContext = FilterContext & {
  fields?: IssueField[]
  sprintGroupBy?: 'Stage' | 'Priority'
  meMode?: 'assigned' | 'reported'
}

/** The first-class views. Single source for sidebar, palette, breadcrumbs. */
export type ViewDef = {
  key: string
  title: string
  path: string
  icon: string
  /** Single-letter palette shortcut hint. */
  shortcut: string
  /** The issues the view lists before its chip filters; the sidebar badge is
   *  this pool's length, so badge and view can't drift. */
  pool: (issues: WorkIssue[], ctx: PoolContext) => WorkIssue[]
}

function filterPool(expr: string) {
  const parsed = parseFilter(expr)
  return (issues: WorkIssue[], ctx: PoolContext) => issues.filter((i) => matchesFilter(i, parsed, ctx))
}

/** For views with no badge. */
const noBadge = () => []

const SPRINT_STAGE_ORDER = ['Ready', 'In progress', 'In review', 'Merged', 'Released', 'Released to QA', 'Released to Prod', 'Blocked', 'Waiting']

export function sprintStages(fields?: IssueField[]): string[] {
  return availableStages(SPRINT_STAGE_ORDER, fields?.find((f) => f.name === 'Stage')?.options.map((o) => o.name))
}

export const SPRINT_VIEW: ViewDef = {
  key: 'sprint',
  title: 'Sprint',
  path: '/sprint',
  icon: 'iteration-cw',
  shortcut: 'S',
  pool: (issues, ctx) => {
    const columns: readonly string[] = ctx.sprintGroupBy === 'Priority' ? PRIORITY_ORDER : sprintStages(ctx.fields)
    return issues.filter((i) =>
      !!ctx.currentSprintName && i.sprint === ctx.currentSprintName && i.subIssueCount === 0 &&
      columns.includes(ctx.sprintGroupBy === 'Priority' ? (i.priority ?? 'Backlog') : (i.stage ?? 'Ready')),
    )
  },
}

export const ROADMAP_VIEW: ViewDef = {
  key: 'roadmap',
  title: 'Roadmap',
  path: '/roadmap',
  icon: 'map',
  shortcut: 'R',
  pool: (issues) => issues.filter((i) => i.typeName === 'Feature' && i.stage !== 'Done'),
}

export const HEALTH_VIEW: ViewDef = {
  key: 'health',
  title: 'Health',
  path: '/health',
  icon: 'heart-pulse',
  shortcut: 'H',
  pool: (issues) => issues.filter((i) => healthCategory(i) !== 'none'),
}

export const SHIP_VIEW: ViewDef = {
  key: 'ship',
  title: 'About to ship',
  path: '/ship',
  icon: 'rocket',
  shortcut: 'A',
  pool: (issues) => issues.filter((i) => i.stage === 'Merged' || isReleasedStage(i.stage)),
}

export const VERIFY_VIEW: ViewDef = {
  key: 'verify',
  title: 'Awaiting verification',
  path: '/verify',
  icon: 'shield-check',
  shortcut: 'V',
  pool: (issues, ctx) => {
    const stages = ctx.fields?.find((f) => f.name === 'Stage')?.options.map((o) => o.name) ?? []
    const qa = hasQaGate(stages)
    return issues.filter((i) => isLiveStage(i.stage) || (qa && isQaStage(i.stage)))
  },
}

/** Not in VIEWS — the sidebar renders Insights as its own section — but a
 *  ViewDef so the palette and breadcrumbs treat it first-class. */
export const INSIGHTS_VIEW: ViewDef = {
  key: 'insights',
  title: 'Insights',
  path: '/insights',
  icon: 'chart-line',
  shortcut: 'N',
  // Whole-universe dashboards — a count badge would be meaningless.
  pool: noBadge,
}

export type InsightsLensDef = {
  key: string
  title: string
  path: string
  icon: string
  managerOnly: boolean
}

export const INSIGHTS_LENSES = [
  { key: 'overview', title: 'Overview', path: '/insights', icon: 'chart-line', managerOnly: false },
  { key: 'me', title: 'Me', path: '/insights/me', icon: 'user-round', managerOnly: false },
  { key: 'clients', title: 'Clients', path: '/insights/clients', icon: 'building-2', managerOnly: false },
  { key: 'teams', title: 'Teams', path: '/insights/teams', icon: 'users', managerOnly: false },
  {
    key: 'individuals',
    title: 'Individuals',
    path: '/insights/individuals',
    icon: 'users-round',
    managerOnly: true,
  },
] as const satisfies readonly InsightsLensDef[]

export type InsightsLensKey = (typeof INSIGHTS_LENSES)[number]['key']

export const ME_VIEW: ViewDef = {
  key: 'me',
  title: 'Me',
  path: '/me',
  icon: 'user-round',
  shortcut: 'M',
  pool: (issues, ctx) => issues.filter((i) => ctx.meMode === 'reported'
    ? i.author === ctx.viewerLogin
    : i.assignees.some((a) => a.login === ctx.viewerLogin)),
}

export const TRIAGE_VIEW: ViewDef = {
  key: 'triage',
  title: 'Triage',
  path: '/triage',
  icon: 'inbox',
  shortcut: 'T',
  pool: (issues) => issues.filter((i) => i.stage === 'Triage'),
}

export const BACKLOG_VIEW: ViewDef = {
  key: 'backlog',
  title: 'Backlog',
  path: '/backlog',
  icon: 'circle-dashed',
  shortcut: 'B',
  // Unassigned Backlog/Ready issues with a visible Priority column.
  pool: (issues) => issues.filter((i) =>
    (i.stage === 'Backlog' || i.stage === 'Ready') && PRIORITY_ORDER.some((p) => p === (i.priority ?? 'Backlog')),
  ),
}

export const MY_VERIFICATIONS_VIEW: ViewDef = {
  key: 'my-verifications',
  title: 'My verifications',
  path: '/my-verifications',
  icon: 'user-round-check',
  shortcut: 'I',
  pool: filterPool('is:open verifier:@me'),
}

export const ROLLOUTS_VIEW: ViewDef = {
  key: 'rollouts',
  title: 'Rollouts',
  path: '/rollouts',
  icon: 'database-zap',
  shortcut: 'O',
  // Open rollouts are badged; the route also overlays recently closed ones.
  pool: (issues) => issues.filter((i) => i.state === 'OPEN' && isRollout(i.labels)),
}

export const VIEWS: ViewDef[] = [
  ME_VIEW,
  {
    key: 'team',
    title: 'My team',
    path: '/team',
    icon: 'users',
    shortcut: 'Y',
    pool: noBadge, // per-area; the sidebar shows the active area's count
  },
  TRIAGE_VIEW,
  BACKLOG_VIEW,
  SPRINT_VIEW,
  ROADMAP_VIEW,
  HEALTH_VIEW,
  SHIP_VIEW,
  VERIFY_VIEW,
  MY_VERIFICATIONS_VIEW,
  ROLLOUTS_VIEW,
  {
    key: 'closed',
    title: 'Recently closed',
    path: '/closed',
    icon: 'circle-check',
    shortcut: 'C',
    // Closed issues live outside the open universe — no live count.
    pool: noBadge,
  },
]

export function viewByPath(pathname: string): ViewDef | undefined {
  return [...VIEWS, INSIGHTS_VIEW].find(
    (v) => pathname === v.path || pathname.startsWith(v.path + '/'),
  )
}

export type SavedSearchGroup = {
  title: string
  icon: string
  items: Array<{ label: string; filter: string }>
}

/** Static saved-search groups. Option order defers to the live GitHub field via
 * the shared field-order rules (only Client is alpha-sorted), so the sidebar
 * matches the facet bar and the sheet. */
export function savedSearchGroups(opts: {
  teams: string[]
  clients: string[]
  products: string[]
  sources: string[]
}): SavedSearchGroup[] {
  const clients = orderOptions('client', opts.clients)
  const products = orderOptions('product', opts.products)
  const sources = orderOptions('source', opts.sources)
  return [
    {
      title: 'By team',
      icon: 'users',
      items: opts.teams.map((a) => ({
        label: areaLabel(a),
        filter: `label:area/${a}`,
      })),
    },
    {
      title: 'By product',
      icon: 'package',
      items: products.map((p) => ({ label: p, filter: `product:${p}` })),
    },
    {
      title: 'By client',
      icon: 'building-2',
      items: clients.map((c) => ({ label: c, filter: `client:${c}` })),
    },
    {
      title: 'By source',
      icon: 'antenna',
      items: sources.map((s) => ({ label: s, filter: `source:${s}` })),
    },
    {
      title: 'By severity',
      icon: 'siren',
      items: [
        { label: 'P1', filter: 'is:open priority:P1' },
        { label: 'Client-affecting', filter: 'label:is/client-affecting -stage:Done' },
        { label: 'Security', filter: 'label:is/security -stage:Done' },
        { label: 'Stalled', filter: 'label:system/stalled' },
        { label: 'Escalated', filter: 'label:system/escalated' },
        { label: 'Blocked', filter: 'label:state/blocked' },
        { label: 'Waiting', filter: 'label:state/waiting' },
      ],
    },
  ]
}
